import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, ShoppingCart, Calendar, Trash2, Search, Eye, Truck, Undo2, Lock } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

// Standard byproduct categories
const STANDARD_BYPRODUCTS = ['Bran', 'Germ', 'Unga Baridi', 'Others'];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [packSizes, setPackSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dispatchStatusFilter, setDispatchStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const navigate = useNavigate();
  
  // For dispatch modal
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [finishedGoods, setFinishedGoods] = useState([]);
  const [byproductInventory, setByproductInventory] = useState([]);

  // Form state - NO batch selection for pending orders
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    customer_id: '',
    sales_agent_id: '',
    destination: '',
    factory: ''  // NEW: Factory tag for reporting
  });
  const [items, setItems] = useState([]);  // Each item: { pack_size_id, pack_size_kg, bales, price_per_bale }
  const [byproducts, setByproducts] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, customersRes, agentsRes, packRes, bpRes, fgRes] = await Promise.all([
        api.get('/orders'),
        api.get('/customers'),
        api.get('/agents'),
        api.get('/pack-sizes'),
        api.get('/inventory/byproducts'),
        api.get('/inventory/finished-goods')
      ]);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setAgents(agentsRes.data);
      setPackSizes(packRes.data);
      setByproductInventory(bpRes.data || []);
      setFinishedGoods(fgRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Add a new order item (NO batch selection - assigned during dispatch)
  const addItem = () => {
    setItems([...items, {
      id: Date.now().toString(),
      pack_size_id: '',
      pack_size_kg: 0,
      bales: 0,
      price_per_bale: 0
    }]);
  };

  const updateItem = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id !== itemId) return item;
      
      const updated = { ...item, [field]: value };
      
      // If pack_size_id changes, update pack_size_kg
      if (field === 'pack_size_id') {
        const ps = packSizes.find(p => p.id === value);
        updated.pack_size_kg = ps?.size_kg || 0;
      }
      
      // If field is numeric, parse it
      if (['bales', 'price_per_bale'].includes(field)) {
        updated[field] = parseFloat(value) || 0;
      }
      
      return updated;
    }));
  };

  const removeItem = (itemId) => {
    setItems(items.filter(i => i.id !== itemId));
  };

  const addByproduct = () => {
    setByproducts([...byproducts, {
      id: Date.now().toString(),
      product: '',
      kg: 0,
      price_per_kg: 0
    }]);
  };

  const updateByproduct = (id, field, value) => {
    setByproducts(byproducts.map(bp =>
      bp.id === id ? { ...bp, [field]: field === 'product' ? value : (parseFloat(value) || 0) } : bp
    ));
  };

  const removeByproduct = (id) => {
    setByproducts(byproducts.filter(bp => bp.id !== id));
  };

  const calculateTotal = () => {
    const itemsTotal = items.reduce((sum, i) => sum + (i.bales * i.price_per_bale), 0);
    const byproductsTotal = byproducts.reduce((sum, bp) => sum + (bp.kg * bp.price_per_kg), 0);
    return itemsTotal + byproductsTotal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    // NO batch validation - batch is assigned during dispatch
    const validItems = items.filter(i => i.pack_size_id && i.bales > 0 && i.price_per_bale > 0);
    const validByproducts = byproducts.filter(bp => bp.product && bp.kg > 0 && bp.price_per_kg > 0);
    
    if (validItems.length === 0 && validByproducts.length === 0) {
      toast.error('Please add at least one item or byproduct');
      return;
    }

    try {
      await api.post('/orders', {
        date: format(date, 'yyyy-MM-dd'),
        customer_id: formData.customer_id,
        sales_agent_id: formData.sales_agent_id || null,
        destination: formData.destination || null,
        factory: formData.factory || null,
        items: validItems.map(i => ({
          pack_size_id: i.pack_size_id,
          pack_size_kg: i.pack_size_kg,
          bales: i.bales,
          price_per_bale: i.price_per_bale
        })),
        byproducts: validByproducts.map(bp => ({
          product: bp.product,
          kg: bp.kg,
          price_per_kg: bp.price_per_kg
        }))
      });
      toast.success('Order created as PENDING. Use Dispatch to allocate stock.');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  const resetForm = () => {
    setFormData({ customer_id: '', sales_agent_id: '', destination: '', factory: '' });
    setItems([]);
    setByproducts([]);
    setDate(new Date());
  };

  const handleUndoDispatch = async (orderId) => {
    if (!window.confirm('This will restore all dispatched inventory. Continue?')) return;
    
    try {
      await api.post(`/orders/${orderId}/undo-dispatch`);
      toast.success('Dispatch undone - inventory restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo dispatch');
    }
  };

  const handleDeleteOrder = async (orderId, dispatchStatus) => {
    const msg = dispatchStatus !== 'Pending' 
      ? 'This order has been dispatched. Deleting will restore inventory. Continue?' 
      : 'Delete this pending order?';
    
    if (!window.confirm(msg)) return;
    
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getDispatchStatusBadge = (status) => {
    const variants = {
      'Pending': 'outline',
      'Partially Dispatched': 'warning',
      'Dispatched': 'success'
    };
    const colors = {
      'Pending': 'bg-slate-100 text-slate-700 border-slate-300',
      'Partially Dispatched': 'bg-amber-100 text-amber-700 border-amber-300',
      'Dispatched': 'bg-green-100 text-green-700 border-green-300'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    const colors = {
      'Unpaid': 'bg-red-100 text-red-700',
      'Part Paid': 'bg-amber-100 text-amber-700',
      'Paid': 'bg-emerald-100 text-emerald-700'
    };
    return <Badge className={colors[status] || 'bg-slate-100'}>{status}</Badge>;
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.order_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDispatch = dispatchStatusFilter === 'all' || o.dispatch_status === dispatchStatusFilter;
    const matchesPayment = paymentStatusFilter === 'all' || o.payment_status === paymentStatusFilter;
    return matchesSearch && matchesDispatch && matchesPayment;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Orders</h1>
          <p className="text-slate-500 mt-1">Create pending orders, then dispatch from inventory</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2 self-start" data-testid="new-order-btn">
              <Plus className="h-4 w-4" />
              New Order
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Order (Pending)</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <DatePickerField date={date} onDateChange={setDate} />
                </div>
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                    <SelectTrigger data-testid="order-customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sales Agent (Optional)</Label>
                  <Select value={formData.sales_agent_id || "__none__"} onValueChange={(v) => setFormData({ ...formData, sales_agent_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {agents.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Destination</Label>
                  <Input
                    value={formData.destination}
                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                    placeholder="Delivery location"
                  />
                </div>
              </div>
              
              {/* Factory Tag for Reporting */}
              <div className="space-y-2">
                <Label>Factory Tag (for reporting)</Label>
                <Select value={formData.factory || "__none__"} onValueChange={(v) => setFormData({ ...formData, factory: v === "__none__" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select factory (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not specified</SelectItem>
                    <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                    <SelectItem value="Githunguri">Githunguri</SelectItem>
                    <SelectItem value="Both">Both</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">This is for reporting purposes only. Inventory is deducted based on batch during dispatch.</p>
              </div>

              {/* Order Items - NO BATCH SELECTION */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Order Items</Label>
                    <p className="text-xs text-slate-500">Batch will be assigned during dispatch</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-order-item-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                {items.length === 0 ? (
                  <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
                    <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>No items added</p>
                    <p className="text-xs">Click "Add Item" to select pack size and quantity</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div key={item.id} className="p-3 bg-slate-50 rounded-lg space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-xs">Pack Size *</Label>
                            <Select
                              value={item.pack_size_id}
                              onValueChange={(v) => updateItem(item.id, 'pack_size_id', v)}
                            >
                              <SelectTrigger data-testid={`item-packsize-${item.id}`}>
                                <SelectValue placeholder="Select pack size" />
                              </SelectTrigger>
                              <SelectContent>
                                {packSizes.map((ps) => (
                                  <SelectItem key={ps.id} value={ps.id}>{ps.size_kg}kg</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Bales *</Label>
                            <Input
                              type="number"
                              value={item.bales || ''}
                              onChange={(e) => updateItem(item.id, 'bales', e.target.value)}
                              placeholder="Qty"
                              min="1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Price/Bale (KES) *</Label>
                            <Input
                              type="number"
                              value={item.price_per_bale || ''}
                              onChange={(e) => updateItem(item.id, 'price_per_bale', e.target.value)}
                              placeholder="Price"
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-1 border-t border-slate-200">
                          <span className="text-sm font-medium">
                            Subtotal: {formatCurrency(item.bales * item.price_per_bale)}
                          </span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="text-red-500 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Byproducts */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Byproducts (Optional)</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addByproduct}>
                    <Plus className="h-4 w-4 mr-1" /> Add Byproduct
                  </Button>
                </div>
                
                {byproducts.map((bp) => (
                  <div key={bp.id} className="p-3 bg-amber-50 rounded-lg space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Byproduct *</Label>
                        <Select value={bp.product} onValueChange={(v) => updateByproduct(bp.id, 'product', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {STANDARD_BYPRODUCTS.map((name) => (
                              <SelectItem key={name} value={name}>{name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Quantity (kg) *</Label>
                        <Input
                          type="number"
                          value={bp.kg || ''}
                          onChange={(e) => updateByproduct(bp.id, 'kg', e.target.value)}
                          placeholder="Kg"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Price/kg (KES) *</Label>
                        <Input
                          type="number"
                          value={bp.price_per_kg || ''}
                          onChange={(e) => updateByproduct(bp.id, 'price_per_kg', e.target.value)}
                          placeholder="Price"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-1 border-t border-amber-200">
                      <span className="text-sm font-medium">
                        Subtotal: {formatCurrency(bp.kg * bp.price_per_kg)}
                      </span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeByproduct(bp.id)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="pt-4 border-t flex justify-between items-center">
                <span className="text-lg font-semibold">Order Total:</span>
                <span className="text-2xl font-bold text-[#1B4D3E]">{formatCurrency(calculateTotal())}</span>
              </div>

              <Button type="submit" className="w-full btn-primary" data-testid="create-order-submit">
                Create Pending Order
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by customer or order number..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="orders-search"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs text-slate-500">Dispatch Status</Label>
              <Select value={dispatchStatusFilter} onValueChange={setDispatchStatusFilter}>
                <SelectTrigger data-testid="dispatch-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Partially Dispatched">Partially Dispatched</SelectItem>
                  <SelectItem value="Dispatched">Dispatched</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-slate-500">Payment Status</Label>
              <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
                <SelectTrigger data-testid="payment-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Unpaid">Unpaid</SelectItem>
                  <SelectItem value="Part Paid">Part Paid</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Dispatch</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    No orders found
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders.map((order) => (
                  <TableRow key={order.id} className="cursor-pointer hover:bg-slate-50" data-testid={`order-row-${order.id}`}>
                    <TableCell className="font-medium">{order.order_number}</TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.customer_name}</p>
                        {order.destination && <p className="text-xs text-slate-500">{order.destination}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      {order.factory ? (
                        <Badge variant="outline">{order.factory}</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(order.order_total)}</TableCell>
                    <TableCell>
                      {order.is_closed && (
                        <Badge className="bg-slate-700 text-white" data-testid={`order-closed-badge-${order.id}`}>
                          <Lock className="h-3 w-3 mr-1" />
                          Closed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{getDispatchStatusBadge(order.dispatch_status || 'Pending')}</TableCell>
                    <TableCell>{getPaymentStatusBadge(order.payment_status || 'Unpaid')}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/orders/${order.id}`)}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {/* Show Dispatch button if not fully dispatched */}
                        {order.dispatch_status !== 'Dispatched' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/orders/${order.id}?dispatch=true`)}
                            title="Dispatch"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Truck className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {/* Show Undo button if dispatched */}
                        {order.dispatch_status !== 'Pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUndoDispatch(order.id)}
                            title="Undo Dispatch"
                            className="text-amber-600 hover:text-amber-700"
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteOrder(order.id, order.dispatch_status)}
                          className="text-red-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Pending Orders</p>
            <p className="text-2xl font-bold text-slate-700">
              {orders.filter(o => o.dispatch_status === 'Pending' || !o.dispatch_status).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-700">Partially Dispatched</p>
            <p className="text-2xl font-bold text-amber-800">
              {orders.filter(o => o.dispatch_status === 'Partially Dispatched').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="pt-6">
            <p className="text-sm text-green-700">Fully Dispatched</p>
            <p className="text-2xl font-bold text-green-800">
              {orders.filter(o => o.dispatch_status === 'Dispatched').length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-6">
            <p className="text-sm text-emerald-700">Dispatched Revenue</p>
            <p className="text-2xl font-bold text-emerald-800">
              {formatCurrency(
                orders
                  .filter(o => o.dispatch_status !== 'Pending' && o.dispatch_status)
                  .reduce((sum, o) => sum + (o.order_total || 0), 0)
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
